/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../platform/Platform';
import {
  AliasType,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../../registry/Component.type';
import { ItemType } from '../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../registry/Components';
import { defaultValue } from './Device';

const DeviceManager: ComponentManagerFn<'Device'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'Device'>(platform, 'Device'),
    type: 'Device',
    name: 'Device',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        category: 'Data',
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
          'TransientDataStore',
        ],
      },
      idAttribute: {
        label: 'ID Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String', 'Integer'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
      nameAttribute: {
        label: 'Name Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
      xAttribute: {
        label: 'X Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Integer', 'Double'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
      yAttribute: {
        label: 'Y Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Integer', 'Double'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
      iconAttribute: {
        label: 'Icon Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
    },
    getDataSourceAliasProperty: (type: AliasType) =>
      type === 'STORE' ? { aliasProp: 'alias' } : undefined,
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'idAttribute':
        case 'nameAttribute':
        case 'xAttribute':
        case 'yAttribute':
        case 'iconAttribute':
          return !!itemData.alias;
        default:
          return true;
      }
    },
    replaceableWith: ['Tracker', 'Area', 'Pulse'],
    getDisplayText: (item) => item.props.nameAttribute,
    category: 'IoT',
    icon: { iconName: 'draw-polygon' },
    description: `**Overview**<br/>
The Device component is a specialized sub-component designed for the **IoT Image Mapper**. Unlike the **Area** component which draws zones (polygons), the Device component renders distinct point-of-interest markers—represented as icons—at specific coordinates on the map.
Use this component to visualize discrete physical assets, such as sensors, cameras, desks, or machinery, on a floor plan or schematic.

**1. Data Connection**<br/>
The component connects to a Data Store that holds the inventory of devices to be displayed.
* **Alias:** The Data Store containing the device records.
* **ID Attribute:** The unique identifier for the device (e.g., "Sensor-001").
* **Name Attribute:** The display name used for tooltips or labels (e.g., "Main Entrance Camera").

**2. Positioning**<br/>
To place the device accurately on the image map, the component requires specific coordinate data.
* **X Attribute:** The field containing the horizontal position (Integer or Double).
* **Y Attribute:** The field containing the vertical position (Integer or Double).
* **Coordinate System:** These coordinates typically correspond to the pixel locations or relative percentage positions on the background image, ensuring the marker stays pinned to the correct spot regardless of screen size.

**3. Visual Representation**<br/>
* **Icon Attribute:** The field that determines which icon to display for the device.
* **Dynamic Visualization:** By binding this to a database field (e.g., "deviceType"), you can automatically show different symbols for different assets (e.g., a "Video" icon for cameras, a "Wifi" icon for routers, or a "Thermometer" for temperature sensors).

**4. Interactivity**<br/>
* **TriggerOnClick:** You can attach a **TriggerOnClick** child component.
* **Use Case:** Clicking a device icon could open a side panel showing live telemetry data, recent alerts, or a control interface to reboot the device or view a live video feed.

**5. Best Use Cases**<br/>
* **Security Systems:** Plotting the location of CCTV cameras and motion detectors on a building layout to provide a "God's eye view" of site security.
* **Asset Tracking:** Showing the real-time location of high-value equipment (like forklifts or medical carts) on a warehouse or hospital map.
* **Smart Office:** Marking the location of individual "Hot Desks," "Printers," or "Meeting Rooms" on an office floor plan.
* **Factory Floor:** Visualizing the health status of specific machines at their physical stations, using colored icons to indicate "Running," "Idle," or "Error" states.`,
  };
};

export default DeviceManager;
